/**
 * Shared helpers for PMS property allowlist UIs:
 * Manage sync (existing token) + create-token wizard step 2.
 */

/** User-visible copy for Manage sync primary button (disabled tooltip + errors). */
export const MANAGE_SYNC_MSG = {
  selectAtLeastOne: 'Must select at least 1 property to sync',
  eachHostNeedsOne: 'Must select at least 1 property for each host to sync',
  allowlistRowNotResolvable: 'Each selected property needs a listing ID. Reload the list and try again.'
};

/** Synthetic host key when a property row has no `hostId` (grouped last in the allowlist UI). */
export const UNKNOWN_HOST_GROUP_KEY = '__unknown__';

export function allowlistHostGroupKey(row) {
  if (!row || row.hostId == null || String(row.hostId) === '') return UNKNOWN_HOST_GROUP_KEY;
  return String(row.hostId);
}

/**
 * Maps + counts used for checkbox rules (per-host minimum, etc.).
 */
export function buildAllowlistSelectionDerived(propertyRows, selectedIds) {
  const rows = propertyRows || [];
  const selected = selectedIds || [];
  const selectedIdSet = new Set(selected.map(String));
  const rowById = new Map();
  rows.forEach(r => rowById.set(String(r.id), r));
  const selectedCountByHost = new Map();
  for (const row of rows) {
    if (!selectedIdSet.has(String(row.id))) continue;
    const hk = allowlistHostGroupKey(row);
    selectedCountByHost.set(hk, (selectedCountByHost.get(hk) || 0) + 1);
  }
  return { selectedIdSet, rowById, selectedCountByHost };
}

/**
 * Ids to keep when “clear all” runs under minimum-selection rules (one per host or one global).
 */
export function minimumAllowlistSelectionIds(propertyRows, { enforceMinSelected, enforcePerHostMinSelected }) {
  const rows = propertyRows || [];
  if (!rows.length) return [];
  if (enforcePerHostMinSelected) {
    const seen = new Set();
    const keep = [];
    for (const row of rows) {
      const key = allowlistHostGroupKey(row);
      if (seen.has(key)) continue;
      seen.add(key);
      keep.push(row.id);
    }
    return keep;
  }
  if (enforceMinSelected) return [rows[0].id];
  return [];
}

/**
 * Primary action state for Manage sync: every host that has rows must have ≥1 selected property.
 */
export function getManageSyncPrimaryButtonState(propertyRows, selectedIds) {
  const rows = propertyRows || [];
  const selected = selectedIds || [];
  if (rows.length === 0) {
    return { disabled: true, disabledReason: null, hostMissingSelection: false };
  }
  const selectedSet = new Set(selected.map(String));
  const hostIdsWithRows = [...new Set(rows.filter(r => r.hostId).map(r => String(r.hostId)))];
  const hostsWithSelection = new Set(rows.filter(r => r.hostId && selectedSet.has(String(r.id))).map(r => String(r.hostId)));
  const hostMissingSelection = hostIdsWithRows.some(hid => !hostsWithSelection.has(hid));
  const disabled = selected.length === 0 || hostMissingSelection;
  let disabledReason = null;
  if (selected.length === 0) disabledReason = MANAGE_SYNC_MSG.selectAtLeastOne;
  else if (hostMissingSelection) disabledReason = MANAGE_SYNC_MSG.eachHostNeedsOne;
  return { disabled, disabledReason, hostMissingSelection };
}

export function normalizeExtAuthDetailForAllowlist(data) {
  if (!data || !data._id) return null;
  const ext = data;
  return {
    ...ext,
    extAccesses: (ext.extAccesses || []).map(access => ({
      _id: String(access._id),
      externalId: access.externalId,
      hostId: access.host && access.host._id ? String(access.host._id) : null,
      hostName: access.host && access.host.name,
      updatedAt: access.updatedAt || null,
      properties: (access.properties || []).map(prop => ({
        _id: prop.externalId,
        listingPropertyId: prop.listingPropertyId != null ? String(prop.listingPropertyId) : null,
        name: prop.propertyName,
        externalId: prop.externalId,
        rooms: (prop.rooms || []).map(r => ({
          _id: r.externalId,
          externalId: r.externalId,
          name: r.roomTypeName
        })),
        units: (prop.units || []).map(u => ({
          _id: u.externalId,
          externalId: u.externalId,
          name: u.unitName
        }))
      }))
    }))
  };
}

/**
 * API compound row id: `hostOid:listingPropertyOid` (not our legacy `hostOid__pmsCode` keys).
 */
function isCompoundAllowlistRowId(idStr) {
  if (!idStr || typeof idStr !== 'string') return false;
  if (idStr.includes('__')) return false;
  const i = idStr.indexOf(':');
  return i > 0 && i < idStr.length - 1;
}

/**
 * Build `propertyAllowlist.items` for pull-new-properties / create-token.
 * Each item must disambiguate the listing: `externalId` and/or `listingPropertyId` and/or compound `id`;
 * never `{ hostId }` alone when the mapping has multiple properties.
 */
export function buildPropertyAllowlistItems(selectedRows) {
  return (selectedRows || [])
    .map(row => {
      const rid = row.id != null ? String(row.id) : '';
      let hostId = row.hostId != null && String(row.hostId) !== '' ? String(row.hostId) : '';
      let listingPropertyId =
        row.listingPropertyId != null && String(row.listingPropertyId).trim() !== '' ? String(row.listingPropertyId).trim() : '';
      const externalId =
        row.externalId != null && String(row.externalId).trim() !== '' && String(row.externalId) !== '—' ? String(row.externalId).trim() : '';

      if (isCompoundAllowlistRowId(rid)) {
        const colon = rid.indexOf(':');
        const h = rid.slice(0, colon);
        const lip = rid.slice(colon + 1);
        if (!hostId) hostId = h;
        if (!listingPropertyId) listingPropertyId = lip;
      }

      const item = {};
      if (hostId) item.hostId = hostId;
      if (externalId) item.externalId = externalId;
      if (listingPropertyId) item.listingPropertyId = listingPropertyId;
      if (isCompoundAllowlistRowId(rid)) item.id = rid;

      const disambiguated = !!(externalId || listingPropertyId || isCompoundAllowlistRowId(rid));
      if (!disambiguated || (!item.hostId && !item.id)) {
        return null;
      }
      return item;
    })
    .filter(Boolean);
}

export function normalizePullCandidateRows(data) {
  if (!data) return [];
  const raw = data.properties || (data.data && data.data.properties);
  const list = Array.isArray(raw) ? raw : Array.isArray(data) ? data : [];
  return list.map((c, i) => {
    const hostId = c.hostId != null ? String(c.hostId) : c.host && c.host._id != null ? String(c.host._id) : '';
    const ext = c.externalId != null && c.externalId !== '' ? String(c.externalId) : '';
    const name = c.name || c.propertyName || '—';
    let listingPropertyId = c.listingPropertyId != null && String(c.listingPropertyId) !== '' ? String(c.listingPropertyId) : '';
    let rawId = c.id != null && String(c.id) !== '' ? String(c.id) : '';
    if (!listingPropertyId && isCompoundAllowlistRowId(rawId)) {
      listingPropertyId = rawId.slice(rawId.indexOf(':') + 1);
    }
    const rowId =
      rawId || (hostId && listingPropertyId ? `${hostId}:${listingPropertyId}` : '') || (hostId && ext ? `${hostId}__${ext}` : '') || `p_${i}`;
    const hostName = (c.hostName != null && String(c.hostName).trim()) || (c.host && (c.host.name || c.host.title || c.host.displayName)) || '';
    const hostExternalId =
      c.hostExternalId != null ? String(c.hostExternalId) : c.host && c.host.externalId != null ? String(c.host.externalId) : null;
    const row = {
      id: rowId,
      name,
      externalId: ext || '—',
      hostId,
      hostName,
      hostExternalId,
      alreadyOnToken: !!(c.alreadyOnToken || c.alreadySynced || c.isOnToken)
    };
    if (listingPropertyId) {
      row.listingPropertyId = listingPropertyId;
    }
    return row;
  });
}

export function pullPropertyDedupeKey(row) {
  if (!row || !row.hostId) return null;
  const ex = row.externalId != null && row.externalId !== '' && row.externalId !== '—' ? String(row.externalId).trim() : '';
  const lp = row.listingPropertyId != null && String(row.listingPropertyId).trim() !== '' ? String(row.listingPropertyId).trim() : '';

  // Prefer stable PMS externalId when available, otherwise fall back to listingPropertyId.
  if (ex) return `${String(row.hostId)}::${ex}`;
  if (lp) return `${String(row.hostId)}::${lp}`;
  return null;
}

/**
 * Stable list order for allowlist tables: synced rows first, then by external id (numeric-aware), then name.
 */
export function compareAllowlistRowsForDisplay(a, b) {
  const ao = !!a.alreadyOnToken;
  const bo = !!b.alreadyOnToken;
  if (ao !== bo) return ao ? -1 : 1;

  const normEx = v => {
    const s = v != null ? String(v).trim() : '';
    if (s === '' || s === '—') return null;
    return s;
  };
  const ea = normEx(a.externalId);
  const eb = normEx(b.externalId);
  if (ea == null && eb != null) return 1;
  if (eb == null && ea != null) return -1;
  if (ea != null && eb != null) {
    const extCmp = ea.localeCompare(eb, undefined, { numeric: true, sensitivity: 'base' });
    if (extCmp !== 0) return extCmp;
  }
  return String(a.name || '').localeCompare(String(b.name || ''));
}

export function mergeSyncedDetailRowsWithPullCandidates(syncedRows, apiRows) {
  const tokenKeySet = new Set();
  syncedRows.forEach(r => {
    const k = pullPropertyDedupeKey(r);
    if (k) tokenKeySet.add(k);
  });
  const mergedById = new Map();
  syncedRows.forEach(r => {
    mergedById.set(String(r.id), { ...r, alreadyOnToken: true });
  });
  apiRows.forEach(r => {
    const rid = String(r.id);
    const byId = mergedById.has(rid);
    const k = pullPropertyDedupeKey(r);
    const byKey = !!(k && tokenKeySet.has(k));
    const onToken = byId || byKey;
    if (onToken) return;
    if (!mergedById.has(rid)) mergedById.set(rid, { ...r, alreadyOnToken: false });
  });
  const rows = Array.from(mergedById.values());
  rows.sort(compareAllowlistRowsForDisplay);
  return rows;
}

export function enrichPullRowsWithHostFromDetail(rows, extDetail) {
  const map = new Map();
  (extDetail.extAccesses || []).forEach(a => {
    const hid = a.hostId != null ? String(a.hostId) : '';
    if (!hid) return;
    map.set(hid, {
      hostName: a.hostName || '',
      hostExternalId: a.externalId != null ? String(a.externalId) : null
    });
  });
  return rows.map(r => {
    const hid = r.hostId != null ? String(r.hostId) : '';
    const m = hid ? map.get(hid) : null;
    const hasName = r.hostName != null && String(r.hostName).trim() !== '';
    const hostName = hasName ? String(r.hostName).trim() : (m && m.hostName) || '';
    const hostExternalId = r.hostExternalId != null ? r.hostExternalId : m ? m.hostExternalId : null;
    return { ...r, hostName, hostExternalId };
  });
}

export function buildFallbackPropertyRowsFromDetail(extDetail) {
  const rows = [];
  (extDetail.extAccesses || []).forEach(access => {
    const hostId = access.hostId;
    const hostName = access.hostName || '';
    const hostExternalId = access.externalId != null ? String(access.externalId) : null;
    (access.properties || []).forEach((p, idx) => {
      const ex = p.externalId || p._id;
      const extDisplay = ex != null && ex !== '' ? String(ex) : '—';
      const listingPropertyId = p.listingPropertyId != null && String(p.listingPropertyId) !== '' ? String(p.listingPropertyId) : '';
      const rowId =
        hostId && listingPropertyId
          ? `${hostId}:${listingPropertyId}`
          : hostId && ex != null && ex !== ''
          ? `${hostId}__${String(ex)}`
          : `fallback_${String(access._id)}_${idx}`;
      const row = {
        id: rowId,
        name: p.name || '—',
        externalId: extDisplay,
        hostId: String(hostId || ''),
        hostName,
        hostExternalId,
        alreadyOnToken: true
      };
      if (listingPropertyId) {
        row.listingPropertyId = listingPropertyId;
      }
      rows.push(row);
    });
  });
  return rows;
}

/**
 * Minimal extAccess-shaped list for host-group headers in the create-token wizard (new mapping, no extAuth yet).
 * @param {(host: object) => string} mapExternalId
 */
export function buildExtAccessesStubsFromWizardHosts(rawList, parentHostId, childHostIds, mapExternalId) {
  const mapEx = typeof mapExternalId === 'function' ? mapExternalId : () => '';
  if (!parentHostId) return [];
  const parent = (rawList || []).find(h => String(h._id) === String(parentHostId));
  if (!parent) return [];
  const childSet = new Set((childHostIds || []).map(String));
  const children = (parent.children || []).filter(c => childSet.has(String(c._id)));
  const accesses = [
    {
      _id: `stub_${parent._id}`,
      hostId: String(parent._id),
      hostName: parent.name || '',
      externalId: mapEx(parent),
      properties: []
    }
  ];
  children.forEach(c => {
    accesses.push({
      _id: `stub_${c._id}`,
      hostId: String(c._id),
      hostName: c.name || '',
      externalId: mapEx(c),
      properties: []
    });
  });
  return accesses;
}
